import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChatClient } from "@azure/communication-chat";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

/**
 * ConversationTab (ACS Chat)
 *
 * Features
 *  - Renders messages for a selected thread
 *  - Real-time updates (message received, participants added/removed)
 *  - Send messages and mark read receipts
 *  - Works with either a `threadId` prop OR a shared `localStorage.activeThreadId`
 *
 * Env / Storage
 *  - ACS endpoint via VITE_ACS_ENDPOINT / REACT_APP_ACS_ENDPOINT / window.__ACS_ENDPOINT__
 *  - Agent token in localStorage.ACSIdentityToken
 *  - Agent id in localStorage.acsIdentity (or ACSIdentity)
 */

//const ACS_ENDPOINT = process.env.REACT_APP_ACS_ENDPOINT;
const ACS_ENDPOINT = "https://ai-sms-mt-dev.unitedstates.communication.azure.com/"

export default function ConversationTab({ threadId: externalThreadId }) {
  const [threadId, setThreadId] = useState(
    externalThreadId || localStorage.getItem("activeThreadId") || ""
  );
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [input, setInput] = useState("");

  const chatClientRef = useRef(null);
  const threadClientRef = useRef(null);
  const realtimeStartedRef = useRef(false);
  const bottomRef = useRef(null);

  const token = useMemo(() => localStorage.getItem("ACSIdentityToken"), []);
  const myUserId = useMemo(
    () => localStorage.getItem("acsIdentity") || localStorage.getItem("ACSIdentity") || "",
    []
  );

  // Keep local threadId in sync with prop
  useEffect(() => {
    if (externalThreadId && externalThreadId !== threadId) {
      setThreadId(externalThreadId);
    }
  }, [externalThreadId]);

  // Also listen for changes to localStorage.activeThreadId (e.g., selected in the left panel)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "activeThreadId") {
        setThreadId(e.newValue || "");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Init ChatClient once
  useEffect(() => {
    (async () => {
      if (!token) {
        setError("Missing ACSIdentityToken in localStorage.");
        return;
      }
      if (!ACS_ENDPOINT) {
        setError("Missing ACS endpoint (VITE_ACS_ENDPOINT / REACT_APP_ACS_ENDPOINT / window.__ACS_ENDPOINT__).");
        return;
      }
      try {
        const credential = new AzureCommunicationTokenCredential(token);
        const chatClient = new ChatClient(ACS_ENDPOINT, credential);
        chatClientRef.current = chatClient;

        if (!realtimeStartedRef.current) {
          await chatClient.startRealtimeNotifications();
          realtimeStartedRef.current = true;
        }
      } catch (e) {
        console.error("ConversationTab: init error", e);
        setError(e?.message || String(e));
      }
    })();
  }, []);

  // Load thread + subscribe to events when threadId changes
  useEffect(() => {
    (async () => {
      if (!threadId || !chatClientRef.current) {
        threadClientRef.current = null;
        setMessages([]);
        setParticipants([]);
        return;
      }
      setLoading(true);
      setError("");

      try {
        const threadClient = chatClientRef.current.getChatThreadClient(threadId);
        threadClientRef.current = threadClient;

        // Load participants
        const ppl = [];
        for await (const p of threadClient.listParticipants()) {
          ppl.push(p);
        }
        setParticipants(ppl);

        // Load recent messages (last ~100)
        const items = [];
        for await (const m of threadClient.listMessages()) {
          // Filter for visible chat content types; keep system events minimal
          if (m.type === "text" || m.type === "html") {
            items.push(m);
          } else if (m.type === "topicUpdated") {
            items.push({ ...m, system: true, content: { message: "Topic updated" } });
          }
        }
        items.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
        setMessages(items.slice(-100));

        // Mark last as read
        const last = items[items.length - 1];
        if (last?.id) {
          try { await threadClient.sendReadReceipt({ chatMessageId: last.id }); } catch {}
        }

        // Subscribe to realtime events
        const onMessage = (e) => {
          if (e.threadId !== threadId) return;
          setMessages((prev) => [
            ...prev,
            {
              id: e.id,
              type: e.type,
              content: { message: e.message },
              senderDisplayName: e.senderDisplayName,
              sender: e.sender,
              createdOn: e.createdOn,
            },
          ]);
          // Mark read
          threadClient.sendReadReceipt({ chatMessageId: e.id }).catch(() => {});
        };
        const onParticipantsAdded = (e) => {
          if (e.threadId !== threadId) return;
          setParticipants((prev) => [...prev, ...e.participantsAdded]);
        };
        const onParticipantsRemoved = (e) => {
          if (e.threadId !== threadId) return;
          setParticipants((prev) => prev.filter((p) =>
            !e.participantsRemoved.some((r) => idEq(r?.id, p?.id))
          ));
        };

        chatClientRef.current.on("chatMessageReceived", onMessage);
        chatClientRef.current.on("participantsAdded", onParticipantsAdded);
        chatClientRef.current.on("participantsRemoved", onParticipantsRemoved);

        return () => {
          try {
            chatClientRef.current?.off("chatMessageReceived", onMessage);
            chatClientRef.current?.off("participantsAdded", onParticipantsAdded);
            chatClientRef.current?.off("participantsRemoved", onParticipantsRemoved);
          } catch {}
        };
      } catch (e) {
        console.error("ConversationTab: load thread error", e);
        setError(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [threadId]);

  // Auto-scroll to bottom when new messages come in
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, threadId]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || !threadClientRef.current) return;
    try {
      const result = await threadClientRef.current.sendMessage({ content: txt });
      // Optimistic append; listMessages will also include it later
      setMessages((prev) => [
        ...prev,
        {
          id: result.id || `${Date.now()}`,
          type: "text",
          content: { message: txt },
          sender: { communicationUserId: myUserId },
          senderDisplayName: "You",
          createdOn: new Date().toISOString(),
        },
      ]);
      setInput("");
    } catch (e) {
      console.error("send error", e);
      setError(e?.message || String(e));
    }
  };

  const me = { communicationUserId: myUserId };

  if (!threadId) {
    return (
      <EmptyState title="No conversation selected" subtitle="Pick a chat from the left panel to start." />
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Header threadId={threadId} participants={participants} />
      <Divider />
      {error && (
        <Box sx={{ p: 2 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2, background: "#fafafa" }}>
        {loading && (
          <Stack alignItems="center" sx={{ my: 2 }}>
            <CircularProgress size={20} />
          </Stack>
        )}
        <Stack spacing={1.25}>
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              isMe={idEq(m?.sender, me)}
            />
          ))}
          <div ref={bottomRef} />
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <IconButton color="primary" onClick={send} disabled={!input.trim()}>
            <SendRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}

function Header({ threadId, participants }) {
  return (
    <Box sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1">Conversation</Typography>
        <Chip size="small" label={shortId(threadId)} sx={{ fontFamily: "ui-monospace, monospace" }} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {participants?.length || 0} participants
        </Typography>
      </Stack>
    </Box>
  );
}

function MessageBubble({ message, isMe }) {
  const text = message?.content?.message || message?.content?.plainText || "";
  const who = message?.senderDisplayName || message?.sender?.communicationUserId || "Unknown";
  const time = formatTime(message?.createdOn);

  return (
    <Stack direction={isMe ? "row-reverse" : "row"}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: "70%",
          p: 1,
          px: 1.25,
          bgcolor: isMe ? "primary.main" : "#fff",
          color: isMe ? "primary.contrastText" : "text.primary",
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          {who} • {time}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{text}</Typography>
      </Paper>
    </Stack>
  );
}

function EmptyState({ title = "", subtitle = "" }) {
  return (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            const tid = localStorage.getItem("activeThreadId");
            if (tid) window.dispatchEvent(new StorageEvent("storage", { key: "activeThreadId", newValue: tid }));
          }}
        >
          Refresh selection
        </Button>
      </Stack>
    </Box>
  );
}

// Helpers
function idEq(a, b) {
  const au = a?.communicationUserId || a?.rawId || a?.id?.communicationUserId;
  const bu = b?.communicationUserId || b?.rawId || b?.id?.communicationUserId;
  return au && bu && au === bu;
}

function shortId(id) {
  if (!id) return "";
  if (id.length <= 8) return id;
  return `${id.slice(0, 4)}…${id.slice(-3)}`;
}

function formatTime(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
